import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Icon,
  Modal,
  notification,
  Spinner,
  Switch,
  Tag,
  Text,
  Title,
} from "components/common";
import PageLayout from "components/common/pageLayout/pageLayout";
import FollowButton from "components/custom/followButton";
import FeedItem from "components/custom/feedItem";
import DemoDataToggle from "components/custom/demoDataToggle";
import { UserAvatar, badgeColor } from "components/custom/userListRow";
import {
  useGetProfileSharedContentQuery,
  useGetSocialPrefsQuery,
  useGetSocialProfileQuery,
  useLazyGetUserActivityQuery,
  useUpdateSocialPrefsMutation,
} from "features/community/apis";
import { demoProfile, demoProfileActivity, demoSharedScripts } from "features/community/mockData/social";
import {
  getUserComparePath,
  getUserFollowersPath,
  getUserFollowingPath,
} from "constants/app-paths";
import styles from "./Profile.module.less";

const formatNumber = (value) =>
  value === null || value === undefined ? null : Number(value).toLocaleString();

const relativeDate = (value) => {
  if (!value) return "Recently";
  const difference = Date.now() - new Date(value).getTime();
  const minutes = Math.max(0, Math.floor(difference / 60000));
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

const StatCard = ({ icon, label, description, value, suffix = "" }) => (
  <div className={styles.statCard}>
    <span className={styles.statIcon}>
      <Icon icon={icon} size={17} />
    </span>
    <div>
      <div className={styles.statValue}>
        {value === null || value === undefined ? "Private" : `${formatNumber(value)}${suffix}`}
      </div>
      <div className={styles.statLabel}>{label}</div>
      {description && <div className={styles.statDescription}>{description}</div>}
    </div>
  </div>
);

const PrivacySettings = () => {
  const { data: prefs } = useGetSocialPrefsQuery();
  const [updatePrefs, { isLoading }] = useUpdateSocialPrefsMutation();
  const modalRef = useRef();
  const [draft, setDraft] = useState({});
  const rows = [
    ["show_questions", "Questions answered"],
    ["show_accuracy", "Quiz accuracy"],
    ["show_streak", "Study streak"],
    ["show_posts", "Posts count"],
    ["show_activity", "Recent activity"],
  ];

  const open = () => {
    setDraft(
      Object.fromEntries(rows.map(([key]) => [key, prefs?.[key] !== false]))
    );
    modalRef.current?.setVisible(true);
  };

  const save = async () => {
    const result = await updatePrefs(draft);
    if (result?.error) {
      notification.error({ message: "Could not save visibility settings." });
      return;
    }
    notification.success({ message: "Visibility settings saved." });
    modalRef.current?.setVisible(false);
  };

  return (
    <>
      <button type="button" className={styles.privacyButton} onClick={open}>
        <Icon icon="AiOutlineSetting" size={15} /> Manage profile visibility
      </button>
      <Modal
        ref={modalRef}
        title="Profile visibility"
        description="Choose what other people can see when they visit your profile."
        okText="Save changes"
        cancelText="Cancel"
        width={520}
        loading={isLoading}
        onOk={save}
        onCancel={() => modalRef.current?.setVisible(false)}
      >
        <div className={styles.visibilityModal}>
          {rows.map(([key, label]) => (
            <div key={key} className={styles.visibilityRow}>
              <Text>{label}</Text>
              <Switch
                standalone
                value={draft[key] !== false}
                onChange={(checked) =>
                  setDraft((current) => ({ ...current, [key]: checked }))
                }
              />
            </div>
          ))}
          <Text className={styles.visibilityHint}>
            Private information stays visible to you. Other visitors will see a
            clear privacy message instead.
          </Text>
        </div>
      </Modal>
    </>
  );
};

const SharedScriptCard = ({ script, onOpen }) => (
  <button
    type="button"
    className={styles.sharedScript}
    onClick={() => script.deepLink && onOpen(script.deepLink)}
  >
    {script.coverImage || script.thumbnail ? (
      <img src={script.coverImage || script.thumbnail} alt="" className={styles.scriptThumbnail} />
    ) : (
      <span className={styles.scriptFallback}>
        <Icon icon="AiOutlineBook" size={19} />
      </span>
    )}
    <span className={styles.sharedCopy}>
      <strong>{script.title}</strong>
      <span>{script.specialty || "Study resource"}</span>
      <span>
        {script.estimatedMinutes ? `${script.estimatedMinutes} min read · ` : ""}
        Shared {relativeDate(script.createdAt || script.sharedAt)}
      </span>
      {script.note && <em>{script.note}</em>}
    </span>
    <span className={styles.openScript}>Open script <Icon icon="FiArrowUpRight" size={14} /></span>
  </button>
);

const EmptyState = ({ icon, title, description, action }) => (
  <div className={styles.emptyState}>
    <span className={styles.emptyIcon}><Icon icon={icon} size={20} /></span>
    <strong>{title}</strong>
    <Text>{description}</Text>
    {action}
  </div>
);

const ProfileSkeleton = () => (
  <main className={styles.profilePage} aria-label="Loading profile">
    <div className={`${styles.skeleton} ${styles.heroSkeleton}`} />
    <div className={styles.statsGrid}>
      {Array.from({ length: 6 }).map((_, index) => <div key={index} className={`${styles.skeleton} ${styles.statSkeleton}`} />)}
    </div>
    <div className={styles.contentLayout}>
      <div className={styles.primaryColumn}>
        <div className={`${styles.skeleton} ${styles.contentSkeleton}`} />
        <div className={`${styles.skeleton} ${styles.contentSkeleton}`} />
      </div>
      <div className={`${styles.skeleton} ${styles.sideSkeleton}`} />
    </div>
  </main>
);

const Profile = () => {
  const { userCode } = useParams();
  const navigate = useNavigate();
  const canUseDemoData = process.env.NODE_ENV !== "production";
  const [useDemoData, setUseDemoData] = useState(false);
  const { data: profile, isLoading, isError } = useGetSocialProfileQuery(userCode, {
    skip: !userCode,
    refetchOnMountOrArgChange: true,
  });
  const { data: sharedResponse, isLoading: sharedLoading, isError: sharedError } = useGetProfileSharedContentQuery(
    { userCode },
    { skip: !userCode || !profile }
  );
  const [triggerActivity, { isFetching: activityFetching }] = useLazyGetUserActivityQuery();
  const [activity, setActivity] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [restricted, setRestricted] = useState(false);
  const [activityError, setActivityError] = useState(false);

  const loadActivity = useCallback(
    async (reset = false) => {
      const result = await triggerActivity({
        userCode,
        before: reset ? undefined : nextBefore,
      });
      const response = result?.data;
      if (!response) {
        setActivityError(true);
        return;
      }
      setActivityError(false);
      setRestricted(Boolean(response.restricted));
      setActivity((current) => (reset ? response.data || [] : [...current, ...(response.data || [])]));
      setNextBefore(response.nextBefore || null);
    },
    [nextBefore, triggerActivity, userCode]
  );

  useEffect(() => {
    if (!userCode) return;
    setActivity([]);
    setNextBefore(null);
    setRestricted(false);
    setActivityError(false);
    triggerActivity({ userCode }).then((result) => {
      const response = result?.data;
      if (!response) {
        setActivityError(true);
        return;
      }
      setActivityError(false);
      setRestricted(Boolean(response.restricted));
      setActivity(response.data || []);
      setNextBefore(response.nextBefore || null);
    });
  }, [triggerActivity, userCode]);

  if (isLoading && !useDemoData) {
    return <PageLayout backIcon label={<Title level={3}>Profile</Title>}><ProfileSkeleton /></PageLayout>;
  }
  if ((isError || !profile) && !useDemoData) {
    return <PageLayout backIcon label={<Title level={3}>Profile</Title>}><div className={styles.loadingState}><Text>User not found.</Text></div></PageLayout>;
  }

  const displayedProfile = useDemoData ? demoProfile : profile;
  const displayedActivity = useDemoData ? demoProfileActivity : activity;
  const displayedSharedScripts = useDemoData ? demoSharedScripts : sharedResponse?.data || [];
  const isSelf = displayedProfile.is_self;
  const stats = displayedProfile.stats || {};
  const sharedScripts = displayedSharedScripts;
  const profileName = displayedProfile.name || [displayedProfile.firstname, displayedProfile.lastname].filter(Boolean).join(" ") || "IllnessRx member";
  const role = displayedProfile.badge || (displayedProfile.user_role === "EDITOR" ? "Educator" : "Learner");
  const joined = displayedProfile.joinedAt
    ? new Date(displayedProfile.joinedAt).toLocaleDateString(undefined, { month: "long", year: "numeric" })
    : null;

  return (
    <PageLayout backIcon label={<Title level={3}>Profile</Title>}>
      <main className={styles.profilePage}>
        <section className={styles.profileHero}>
          <div className={styles.cover} />
          <div className={styles.heroContent}>
            <div className={styles.identityRow}>
              <UserAvatar user={displayedProfile} size={88} />
              <div className={styles.identityCopy}>
                <div className={styles.nameRow}>
                  <Title level={3} className={styles.profileName}>{profileName}</Title>
                  <Tag color={badgeColor(role)} type="light" pill>{role}</Tag>
                </div>
                {displayedProfile.username && <Text className={styles.username}>@{displayedProfile.username}</Text>}
                <div className={styles.profileFacts}>
                  {displayedProfile.educationLevel && <span><Icon icon="AiOutlineBook" size={14} /> {displayedProfile.educationLevel}</span>}
                  {displayedProfile.location && <span><Icon icon="HiLocationMarker" size={14} /> {displayedProfile.location}</span>}
                  {joined && <span><Icon icon="AiOutlineCalendar" size={14} /> Joined {joined}</span>}
                </div>
              </div>
              <div className={styles.heroActions}>
                {!isSelf && displayedProfile.user_role !== "ADMIN" && (
                  <>
                    <FollowButton userCode={userCode} relationship={displayedProfile.relationship?.outgoing} variant="outline" />
                    {displayedProfile.relationship?.outgoing === "accepted" && (
                      <Button type="default" text="Compare" onClick={() => navigate(getUserComparePath(userCode))} />
                    )}
                  </>
                )}
                {isSelf && <Button type="default" text="Edit profile" onClick={() => navigate("/accounts")} />}
              </div>
            </div>
            <div className={styles.connectionRow}>
              <button type="button" onClick={() => navigate(getUserFollowersPath(userCode))}><strong>{formatNumber(displayedProfile.followersCount || 0)}</strong> Followers</button>
              <span />
              <button type="button" onClick={() => navigate(getUserFollowingPath(userCode))}><strong>{formatNumber(displayedProfile.followingCount || 0)}</strong> Following</button>
              {isSelf && <PrivacySettings />}
            </div>
          </div>
        </section>

        <section className={styles.statsGrid} aria-label="Profile statistics">
          <StatCard icon="MdOutlineQuiz" label="Questions answered" description="Lifetime total" value={stats.questionsAnswered} />
          <StatCard icon="AiOutlineStar" label="Quiz accuracy" description="Across completed quizzes" value={stats.accuracy} suffix="%" />
          <StatCard icon="HiFire" label="Study streak" description="Current consecutive days" value={stats.streak} suffix=" days" />
          <StatCard icon="BsShare" label="Recommended scripts" description="Learning resources recommended" value={stats.sharedScripts} />
          <StatCard icon="FiUsers" label="Followers" description="People following this learner" value={displayedProfile.followersCount} />
          <StatCard icon="FiUsers" label="Following" description="People this learner follows" value={displayedProfile.followingCount} />
        </section>

        <div className={styles.contentLayout}>
          <div className={styles.primaryColumn}>
            <section className={styles.contentCard}>
              <div className={styles.cardHeader}><Title level={4}>About</Title></div>
              <div className={styles.aboutContent}>
                <div className={styles.aboutIntro}>{displayedProfile.bio || "This learner hasn't added a bio yet."}</div>
                <div className={styles.aboutRow}><span>Education</span><div className={styles.aboutValue}>{displayedProfile.educationLevel || "Not added yet"}</div></div>
                <div className={styles.aboutRow}><span>Specialties</span><div>{displayedProfile.specialties?.length ? displayedProfile.specialties.map((item) => <Tag key={item} color="blue" type="light" pill>{item}</Tag>) : <span className={styles.aboutValue}>Not added yet</span>}</div></div>
                <div className={styles.aboutRow}><span>Interests</span><div>{displayedProfile.interests?.length ? displayedProfile.interests.map((item) => <Tag key={item} color="purple" type="light" pill>{item}</Tag>) : <span className={styles.aboutValue}>Not added yet</span>}</div></div>
                <div className={styles.aboutRow}><span>Location</span><div className={styles.aboutValue}>{displayedProfile.location || "Not added yet"}</div></div>
                <div className={styles.aboutRow}><span>Joined</span><div className={styles.aboutValue}>{joined || "Not available"}</div></div>
                {isSelf && !displayedProfile.bio && <Button type="default" text="Edit profile" onClick={() => navigate("/accounts")} />}
              </div>
            </section>

            <section className={styles.contentCard}>
              <div className={styles.cardHeader}><div><Title level={4}>Recent activity</Title><Text>Learning and community updates</Text></div></div>
              {restricted ? (
                <EmptyState icon="BiHide" title="Activity is private" description="This learner has chosen to keep their activity private." />
              ) : !useDemoData && activityFetching && displayedActivity.length === 0 ? (
                <div className={styles.inlineLoading}><Spinner /></div>
              ) : !useDemoData && activityError ? (
                <EmptyState icon="BiSad" title="Activity could not be loaded" description="Try refreshing this profile to load recent learning activity." />
              ) : displayedActivity.length === 0 ? (
                <EmptyState icon="AiOutlineBook" title="No activity yet" description={isSelf ? "Your learning progress and community activity will appear here." : "This learner has not shared any recent activity."} />
              ) : (
                <div className={styles.activityList}>
                  {displayedActivity.map((item) => <FeedItem key={item.activity_event_id} item={item} />)}
                  {!useDemoData && nextBefore && <button type="button" className={styles.loadMore} onClick={() => loadActivity(false)} disabled={activityFetching}>{activityFetching ? "Loading…" : "Load more activity"}</button>}
                </div>
              )}
            </section>
          </div>

          <aside className={styles.sidebar}>
            <section className={styles.contentCard}>
              <div className={styles.cardHeader}><div><Title level={4}>Recommended scripts</Title><Text>Helpful learning content from {isSelf ? "you" : profileName}</Text></div></div>
              {!useDemoData && sharedLoading ? <div className={styles.inlineLoading}><Spinner /></div> : !useDemoData && sharedError ? <EmptyState icon="BiSad" title="Shared scripts could not be loaded" description="Try refreshing this profile to load shared learning resources." /> : sharedScripts.length ? <div className={styles.sharedList}>{sharedScripts.map((script) => <SharedScriptCard key={script.shareId || script.scriptId} script={script} onOpen={navigate} />)}</div> : <EmptyState icon="BsShare" title="No shared study material" description={isSelf ? "Share a script to help other learners discover it." : "This member has not shared any study material yet."} />}
            </section>
          </aside>
        </div>
        {canUseDemoData && <DemoDataToggle value={useDemoData} onChange={setUseDemoData} label="Profile data" />}
      </main>
    </PageLayout>
  );
};

export default Profile;
